// #336 slice 6, brief §7.3: the text of one cell of the wide editor's grid,
// one exercise in one round read through one lens (mock `cellTxt`). The
// values come from readRound and the reference from roundReferenceKg; the
// numbers are spelled by the Draft line formatter. Keep free of React and
// any UI import.
import { formatDraftNumber, formatDraftValue, type DraftLineOptions } from "./previewFormat.js";
import type { SlotDraft } from "./programEdit.js";
import { setCountAt } from "./programEditRoundSets.js";
import { slotRoundPeak } from "./programIntensityDraft.js";
import { isEffortAxis, readRound, type ReadSet } from "./programProgression.js";
import { referenceLoadable, roundReferenceKg } from "./programProgressionReference.js";
import type { MaxForProposal } from "./resolveDefaultWeight.js";

/** What a grid cell shows: the effort, the reps, the load, or the set count
 *  of that round (brief §15 row 36). */
export type DraftGridLens = "effort" | "reps" | "load" | "sets";

/** A value nobody has typed yet, or one that does not parse. */
const UNSET = "?";
/** A row with no load axis at all, in the effort lens. */
const NO_AXIS = "–";
/** No load to show: no kilos written and no MAX to read one from. */
export const NO_LOAD = "·";

/** A load in the cell's unit as a bare number: the column says the unit. */
const bareLoad = (value: number, o: DraftLineOptions): string =>
  formatDraftValue("absolute", value, { ...o, labels: { ...o.labels, units: { kg: "", lb: "" } } }).trim();

const num = (n: number | null): string => (n === null ? UNSET : formatDraftNumber(n));

const anyAmrap = (sets: readonly ReadSet[]): string => (sets.some((s) => s.amrap) ? "+" : "");

// «4×5», «3×40s», «3×5/5/3+»: the set count, then the reps when they agree
// or each set's when they do not.
const repsText = (sets: readonly ReadSet[], timed: boolean, o: DraftLineOptions): string => {
  const n = sets.length;
  if (timed) return `${n}×${num(sets[0]!.reps)}${o.labels.seconds}`;
  const reps = sets.map((s) => num(s.reps));
  const same = reps.every((r) => r === reps[0]);
  return `${n}×${same ? reps[0]! : reps.join("/")}${anyAmrap(sets)}`;
};

// «8», «7→9», «r2» for RIR, «+» when a set is an AMRAP. A row on another axis
// says which one, so the cell is never read as empty. A row in kilos with a
// MAX says the load of the set that gives the band, and its % of the MAX.
const effortText = (
  slot: SlotDraft,
  w: number,
  sets: readonly ReadSet[],
  timed: boolean,
  o: DraftLineOptions,
  max: MaxForProposal | undefined,
): string => {
  const effort = sets.filter((s) => isEffortAxis(s.kind) && s.value !== null);
  if (effort.length === 0) {
    const kind = sets[0]!.kind;
    if (kind === "absolute") {
      const peak = slotRoundPeak(slot, w, { max, cellUnit: o.cellUnit });
      const kg = peak?.set.kind === "absolute" ? peak.set.value : null;
      if (kg !== null && o.labels.ofMax) return o.labels.ofMax(formatDraftValue("absolute", kg, o), peak!.value);
      return o.labels.units[o.displayUnit];
    }
    if (kind === "percent-of-max") return "%";
    if (kind === "history") return o.labels.history;
    return timed ? o.labels.seconds : NO_AXIS;
  }
  const values = [...new Set(effort.map((s) => num(s.value)))];
  const span = values.length === 1 ? values[0]! : `${values[0]!}→${values[values.length - 1]!}`;
  return `${effort[0]!.kind === "rir" ? "r" : ""}${span}${anyAmrap(sets)}`;
};

// The kilos written on the first set, or «≈X» from the MAX for a row at
// effort or at %, as the round ladder's «ref. ≈ X».
const loadText = (
  slot: SlotDraft,
  w: number,
  sets: readonly ReadSet[],
  o: DraftLineOptions,
  max: MaxForProposal | undefined,
): string => {
  const first = sets[0]!;
  if (first.kind === "absolute") return first.value === null ? UNSET : bareLoad(first.value, o);
  const kg = roundReferenceKg(slot, w, max);
  return kg === null ? NO_LOAD : `≈${bareLoad(referenceLoadable(kg, o.cellUnit), o)}`;
};

/** One exercise in round `w` through `lens`. Null when it does not run in
 *  that round. `max`: the athlete's MAX for the exercise, for the load lens
 *  and for a row in kilos in the effort lens. */
export const formatDraftGridCell = (
  slot: SlotDraft,
  w: number,
  lens: DraftGridLens,
  o: DraftLineOptions,
  max?: MaxForProposal,
): string | null => {
  const timed = slot.setRows[0]?.workKind === "countdown";
  const sets = readRound(slot, w, timed);
  if (sets === null || sets.length === 0) return null;
  if (lens === "sets") return String(setCountAt(slot, w));
  if (lens === "reps") return repsText(sets, timed, o);
  if (lens === "effort") return effortText(slot, w, sets, timed, o, max);
  return loadText(slot, w, sets, o, max);
};
